import { useForm } from 'react-hook-form';

import { detailsModalSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';

import { InferType } from 'yup';
import { useFoldersShortQuery } from '@common/cache';
import { IReport } from '@common/types';
import { useReportStore } from '@store';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsEdit } from '../../../hooks';

type IFormShape = InferType<typeof detailsModalSchema>;

const emptyValues: IFormShape = {
  report_type: '',
  name: '',
  report_folder_id: '',
  description: '',
  private: false,
  allow_others_to_edit: false,
};

export const useDetailsModal = () => {
  const navigate = useNavigate();
  const isEdit = useIsEdit();

  const {
    isModalOpen,
    setIsModalOpen,
    setReport,
    report,
    setFilters,
    setIsFormInitialized,
    isFormInitialized,
    isOwner,
  } = useReportStore();

  const { data: folderOptions = [] } = useFoldersShortQuery();

  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const initialValues: IFormShape = {
    ...emptyValues,
    ...report,
    report_folder_id: report?.report_folder?.id ?? '',
    report_type: report?.report_type as string,
  };

  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    resolver: yupResolver(detailsModalSchema),
    defaultValues: { ...initialValues },
    shouldUnregister: false,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isDirty },
    getValues,
    reset,
  } = formMethods;

  const handleClose = () => {
    if (isDirty) return setIsSaveChangesModalOpen(true);

    if (isFormInitialized) {
      setIsModalOpen(false);
    } else {
      navigate('/reporting');
    }
  };

  const submitHandler = async (submitValues: IFormShape) => {
    const values = {
      ...submitValues,
      report_type: submitValues.report_type ?? report?.report_type,
    };

    const selectedFolder = folderOptions.find(
      o => o.id === values.report_folder_id,
    );

    const selectedFolderWithPrivate = selectedFolder
      ? { ...selectedFolder, private: false }
      : undefined;

    if (values.report_type !== report?.report_type) {
      setFilters([]);
    }

    setReport({
      description: values.description,
      private: values.private,
      allow_others_to_edit: values.allow_others_to_edit,
      report_folder_id: values.report_folder_id,
      name: values.name,
      report_folder: selectedFolderWithPrivate,
      ...(values.report_type !== report?.report_type && {
        report_type: values.report_type as IReport['report_type'],
        options: {
          ...report?.options,
          select: [],
        },
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
    reset({ ...values });
    setIsFormInitialized(true);
    setIsModalOpen(false);
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);

    if (isFormInitialized) {
      setIsModalOpen(false);
      reset(initialValues);
    } else {
      navigate('/reporting');
    }
  };

  const handleSaveChanges = async () => {
    onSubmit();
    setIsSaveChangesModalOpen(false);
  };

  useEffect(() => {
    if (report?.id) {
      reset({ ...initialValues, ...report });
      return;
    }

    if (isModalOpen) {
      reset(getValues());
    }
  }, [report?.id, isModalOpen]);

  return {
    formMethods,
    onSubmit,
    isSubmitting,
    isModalOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    folderOptions: folderOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    isFormInitialized,
    isFolderPrivate: report?.report_folder?.private,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEdit,
    isOwner: !isEdit || isOwner,
  };
};
